import { SubscriberEventConstants } from '../constants/event-constants';
import { INITIAL_SEQUENCE_NUMBER } from '../constants/value-constants';
import { MAX_RETRY_COUNT } from '../constants/value-constants';
import { OUT_OF_SYNC_WAIT_TIME } from '../constants/value-constants';
import fetchHistory from '../utils/fetch-pubnub-history';
import { processChunkMessage } from '../utils/process-messages';
import heapify from './heapify';
class Subscriber {
    processingQueueMap;
    batchedProccesingQueueMap;
    subscriberOptions;
    timer;
    userId;
    retryCount = 0;
    logger;
    constructor(options) {
        this.subscriberOptions = options;
        this.userId = options.userId;
        this.processingQueueMap = {};
        this.batchedProccesingQueueMap = {};
        this.timer = null;
        this.logger = options.logger;
    }
    initAfterReconnect() {
        this.subscriberOptions.pubnubInstance.subscribe({
            channels: [this.subscriberOptions.channelId],
        });
        this.fetchMissingMessages();
    }
    pushToProcessingQueue(msgEvent, userid) {
        if (userid) {
            const userProcessingQueue = this.processingQueueMap[userid] || [];
            const { sequence, sequenceStartTime } = msgEvent.message;
            const sequenceNo = (sequenceStartTime ?? 0) + sequence;
            if (sequenceNo > this.getLastMessageSeqOfUser(userid).sequenceNo) {
                if ('batchSize' in msgEvent.message) {
                    const batchMessage = processChunkMessage(msgEvent, this.batchedProccesingQueueMap);
                    if (batchMessage) {
                        this.processingQueueMap[userid] = heapify(userProcessingQueue, batchMessage);
                    }
                }
                else {
                    this.processingQueueMap[userid] = heapify(userProcessingQueue, msgEvent);
                }
            }
        }
    }
    getLastMessageSeqOfUser = (userId) => {
        const { lastMessagePerUser } = this.subscriberOptions;
        const usersLastMessage = lastMessagePerUser[userId];
        if (usersLastMessage) {
            const { message: { sequence, sequenceStartTime }, } = usersLastMessage;
            return {
                sequenceNo: (sequenceStartTime ?? 0) + sequence,
                sequenceStartTime: sequenceStartTime ?? 0,
            };
        }
        return {
            sequenceNo: INITIAL_SEQUENCE_NUMBER,
            sequenceStartTime: 0,
        };
    };
    getSequenceAndOffSequence(userId) {
        const sequencedMessages = [];
        const userProcessingQueue = this.processingQueueMap[userId] || [];
        let offSeqeuncedMessages = [];
        let { sequenceNo: lastSequenceNo, sequenceStartTime: lastSequenceStartTime } = this.getLastMessageSeqOfUser(userId);
        if (userProcessingQueue.length > 0) {
            userProcessingQueue.forEach(msgEvent => {
                const { sequence, sequenceStartTime = 0 } = msgEvent.message;
                const sequenceNo = (sequenceStartTime ?? 0) + sequence;
                if (lastSequenceStartTime < sequenceStartTime) {
                    sequencedMessages.push(msgEvent);
                    lastSequenceStartTime = sequenceStartTime;
                    lastSequenceNo = sequenceNo;
                    offSeqeuncedMessages = [];
                }
                else if (lastSequenceStartTime === sequenceStartTime) {
                    if (sequenceNo - lastSequenceNo === 1) {
                        sequencedMessages.push(msgEvent);
                        lastSequenceNo = sequenceNo;
                    }
                    else {
                        offSeqeuncedMessages.push(msgEvent);
                    }
                }
            });
        }
        return [sequencedMessages, offSeqeuncedMessages];
    }
    updateUserLastMessage = (userId, sequencedMessages) => {
        const { lastMessagePerUser } = this.subscriberOptions;
        const newLastMessage = sequencedMessages[sequencedMessages.length - 1];
        if (newLastMessage) {
            lastMessagePerUser[userId] = newLastMessage;
        }
    };
    handleSequenceMessages = (userId, sequencedMessages) => {
        this.updateUserLastMessage(userId, sequencedMessages);
        this.handleSuccess(sequencedMessages);
    };
    handleOffSequenceMessaegs = (userId, offSeqeuncedMessages) => {
        if (offSeqeuncedMessages.length > 0) {
            this.logger(SubscriberEventConstants.SUBSCRIBER_OUT_OF_SEQ_MESSAGE, {});
            this.processingQueueMap[userId] = offSeqeuncedMessages;
            if (this.processingQueueMap[userId]?.length) {
                this.awaitSyncMessage();
            }
        }
        else {
            this.processingQueueMap[userId] = [];
            if (this.timer) {
                clearTimeout(this.timer);
            }
        }
    };
    getAllUserIds() {
        return Object.keys(this.processingQueueMap);
    }
    processReceivedMessage() {
        const allMessageUsers = this.getAllUserIds();
        allMessageUsers.forEach(userId => {
            if (userId !== this.userId) {
                const [sequencedMessages, offSeqeuncedMessages] = this.getSequenceAndOffSequence(userId);
                if (sequencedMessages.length > 0) {
                    this.handleSequenceMessages(userId, sequencedMessages);
                }
                this.handleOffSequenceMessaegs(userId, offSeqeuncedMessages);
            }
            else {
                this.logger(SubscriberEventConstants.SUBSCRIBER_MESSAGE_UUID_ABSENT, {
                    channelId: this.subscriberOptions.channelId,
                });
            }
        });
    }
    // starts a timer for the out of sync message receiving for the user id
    async awaitSyncMessage() {
        if (this.timer) {
            clearTimeout(this.timer);
        }
        const handleOnTimeout = () => {
            // TODO:LOGEVENT
            if (this.retryCount < MAX_RETRY_COUNT) {
                this.retryCount++;
                this.fetchMissingMessages();
            }
            else {
                this.handleFailure('Timeout');
            }
        };
        this.timer = setTimeout(handleOnTimeout, OUT_OF_SYNC_WAIT_TIME);
    }
    onRecieve(msgEvent) {
        this.logger(SubscriberEventConstants.SUBSCRIBER_MESSAGE_RECIVED, {
            userId: msgEvent.publisher,
            sequence: msgEvent.message?.sequence,
            sequenceStartTime: msgEvent.message?.sequenceStartTime,
            channelId: this.subscriberOptions.channelId,
        });
        this.processMultipleMessages([
            {
                channel: msgEvent.channel,
                message: msgEvent.message,
                uuid: msgEvent.publisher,
                timetoken: msgEvent.timetoken,
            },
        ]);
    }
    messageListener = {
        message: event => {
            if (event.subscribedChannel === this.subscriberOptions.channelId &&
                event.publisher !== this.userId) {
                this.onRecieve(event);
            }
        },
    };
    addListnerForChannel() {
        this.subscriberOptions.pubnubInstance.addListener(this.messageListener);
    }
    init() {
        this.subscriberOptions.pubnubInstance.subscribe({
            channels: [this.subscriberOptions.channelId],
        });
        this.addListnerForChannel();
    }
    processMultipleMessages = (messages) => {
        messages.forEach(msg => {
            if (!this.subscriberOptions.noHistory) {
                msg.message.sequenceStartTime = 0;
            }
            if (msg.uuid && msg.uuid !== this.userId) {
                this.pushToProcessingQueue(msg, msg.uuid);
            }
        });
        this.processReceivedMessage();
    };
    // TODO: Avoid the loop of fetching messages for each user
    fetchMissingMessages = async () => {
        // TODO:ERROR:SUBSCRIBER (explained below)
        // TODO:FEATURE maintain a counter of calls for a pair of start and end time and after 3 such counts ultimate failure
        const allUsers = this.getAllUserIds();
        allUsers.forEach(async (userId) => {
            if (userId !== this.userId) {
                try {
                    let startTimeToken = this.subscriberOptions.lastMessagePerUser[userId]?.timetoken;
                    let endTimeToken;
                    if (this.processingQueueMap[userId] &&
                        this.processingQueueMap[userId]?.length) {
                        const maxSequenceMessage = this.processingQueueMap[userId]?.reduce((prev, current) => prev && prev.message.sequence > current.message.sequence ? prev : current);
                        if (maxSequenceMessage) {
                            endTimeToken = maxSequenceMessage.timetoken;
                        }
                    }
                    this.logger(SubscriberEventConstants.SUBSCRIBER_OUT_SEQ_FETCH_STARTED, {});
                    if (!endTimeToken) {
                        endTimeToken = startTimeToken;
                        startTimeToken = undefined;
                    }
                    const messages = await fetchHistory(this.subscriberOptions.channelId, this.subscriberOptions.pubnubInstance, startTimeToken, endTimeToken);
                    this.logger(SubscriberEventConstants.SUBSCRIBER_OUT_SEQ_FETCH_SUCCESS, {});
                    this.processMultipleMessages(messages);
                }
                catch (e) {
                    this.logger(SubscriberEventConstants.SUBSCRIBER_OUT_SEQ_FETCH_FAILED, {
                        error: JSON.stringify(e),
                        channelId: this.subscriberOptions.channelId,
                    });
                }
            }
        });
    };
    handleSuccess(messages) {
        if (this.retryCount) {
            this.logger(SubscriberEventConstants.SUBSCRIBER_GOT_SEQ_AFTER_FETCH, {
                retryCount: this.retryCount,
            });
        }
        this.retryCount = 0;
        this.subscriberOptions.onReceiveSucces(messages);
    }
    handleFailure(error) {
        this.logger(SubscriberEventConstants.SUBSCRIBER_RETRY_FAILED, {});
        // TODO: UNSUBSCRIBE and handle data insconsitancy
        this.subscriberOptions.onReceiveFailed(error);
    }
    destroySubscriber() {
        const { pubnubInstance, channelId } = this.subscriberOptions;
        pubnubInstance.unsubscribe({ channels: [channelId] });
        pubnubInstance.removeListener(this.messageListener);
    }
}
export default Subscriber;
//# sourceMappingURL=subscriber.js.map